/** The itinerary part of the tour PATCH (plan §3 compile rules 5–7).
 *
 *  5. The itinerary travels as the full rebuilt tree whenever the draft touches it (a
 *     day / stop / activity field, a membership list, a day's route file): every day in
 *     order, each day's stops in TIME order (the server's order), each stop's activity rows
 *     in order. The funnel reconciles by id per parent — a row without `id` is created, an
 *     omitted one deleted, and a saved row moved to another saved parent keeps its id (the
 *     view relocates it first). A saved row is sent as `{id}` plus only the fields the
 *     draft changed (key-present semantics: an untouched stored text is never re-sent, so
 *     never re-sanitised), and always with its children; a new row carries every field
 *     (its defaults with the draft's entries over them). `index` lists the ids in the
 *     order sent — `days[di]`, `slots[di][si]`, `activities[di][si][ai]` — so a positional
 *     400 (`itinerary.days.2.timeslots.1.time`) maps back to its record.
 *     A plain-field edit elsewhere sends no `itinerary` key at all.
 *  6. Pins are out of band: a saved stop's changed pin is `slot_pins[id]`, a new stop's
 *     pin (when not the default) `slot_pins_by_pos[di][si]` — positions in the submitted
 *     tree, which the view matches against the day's stops in time order after the write.
 *     A pin change alone does not send the tree.
 *  7. A day's staged route file is the `kml_day_<di>` part (`files.kml`); null on a saved
 *     day sends `kml_file: null` (clears it). The tour-level file (B6) is the `kml_file`
 *     part, or `kml_file: null` in the body.
 *
 *  `included` lists the changes the body carries: entries of a saved row the server no
 *  longer has (or of a row no list reaches), and staged files this page no longer holds,
 *  are left out. */
import type { TourUploadFiles, TourWriteInput } from '@/api/tours';
import { parseRecordKey, type DraftEntry, type DraftWireChange, type RecordKey } from '@/features/records/draft/changeSet';
import { isStagedFile, type StagedFiles } from '@/features/records/fields/widgets/stagedFiles';

import type { TourBaseline } from '../record/baselines';

import {
  ACTIVITY,
  ACTIVITY_DEFAULTS,
  activityRec,
  asPin,
  DAY,
  DAY_DEFAULTS,
  DAY_KML,
  dayRec,
  DAYS_PATH,
  DEFAULT_PIN,
  itineraryTree,
  pinPayload,
  pinsEqual,
  SLOT,
  SLOT_DEFAULTS,
  SLOT_PIN,
  slotRec,
  TOUR_KML,
  type Entries,
  type PinPayload,
} from './model';

export interface ItineraryIndex {
  days: number[];
  slots: number[][];
  activities: number[][][];
}

export interface CompiledItinerary {
  /** The keys to merge into the PATCH body (`itinerary`, `slot_pins`, `slot_pins_by_pos`,
   *  `kml_file`). */
  body: TourWriteInput;
  /** Route file parts (`kml`, `tourKml`). */
  files: Pick<TourUploadFiles, 'kml' | 'tourKml'>;
  index: ItineraryIndex;
  included: DraftWireChange[];
  missingFiles: Array<{ path: string; name: string }>;
}

function textWire(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

function htmlWire(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** A price as the funnel takes it: a number (the create path rounds it), null when blank;
 *  text that is not a number is sent as typed (the server's positional 400 names it). */
export function priceWire(v: unknown): number | string | null {
  if (v === null || v === undefined) return null;
  const t = String(v).trim();
  if (t === '') return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : t;
}

const DAY_WIRE: Record<string, (v: unknown) => [string, unknown][]> = {
  'day.title': (v) => [['title', textWire(v)]],
  'day.subtitle': (v) => [['subtitle', textWire(v)]],
  'day.description': (v) => [['description', htmlWire(v)]],
  'day.day_span': (v) => [['day_span', typeof v === 'number' ? v : Number(v)]],
};

function slotWire(isNew: boolean): Record<string, (v: unknown) => [string, unknown][]> {
  return {
    'slot.time': (v) => [['time', `${typeof v === 'string' ? v : ''}:00`]],
    'slot.title': (v) => [['title', textWire(v)]],
    'slot.subtitle': (v) => [['subtitle', textWire(v)]],
    'slot.description': (v) => [['description', htmlWire(v)]],
    'slot.timeline_display': (v) => [['timeline_display', typeof v === 'number' ? v : Number(v ?? 0)]],
    'slot.timeline_text': (v) => [['timeline_text', textWire(v)]],
    'slot.show_transport': (v) => [['show_transport', v === true]],
    // The funnel reads `transport_type` when it creates a stop, `next_transport` after.
    'slot.next_transport': (v) => {
      const id = typeof v === 'number' ? v : null;
      return isNew ? [['transport_type', id], ['next_transport', id]] : [['next_transport', id]];
    },
    'slot.transport_text': (v) => [['transport_text', textWire(v)]],
  };
}

const ACTIVITY_WIRE: Record<string, (v: unknown) => [string, unknown][]> = {
  'activity.activity': (v) => [['activity', typeof v === 'number' ? { id: v } : null]],
  'activity.cost_prefix': (v) => [['cost_prefix', textWire(v)]],
  'activity.cost_min': (v) => [['cost_min', priceWire(v)]],
  'activity.cost_max': (v) => [['cost_max', priceWire(v)]],
  'activity.cost_unit': (v) => [['cost_unit', textWire(v)]],
  'activity.currency': (v) => [['currency', typeof v === 'number' ? v : null]],
  'activity.display_name': (v) => [['display_name', textWire(v)]],
  'activity.display_on_map': (v) => [['display_on_map', v === true]],
};

/** A row's wire fields: every field of a new row (defaults, the draft over them), only
 *  the changed ones of a saved row. */
function rowFields(
  id: number,
  defaults: Readonly<Record<string, unknown>>,
  own: Record<string, DraftEntry> | undefined,
  wire: Record<string, (v: unknown) => [string, unknown][]>,
): Record<string, unknown> {
  const out: Record<string, unknown> = id > 0 ? { id } : {};
  const paths = id > 0 ? Object.keys(own ?? {}) : Object.keys(defaults);
  for (const path of paths) {
    const map = wire[path];
    if (!map) continue;
    const entry = own?.[path];
    const v = entry ? entry.to : defaults[path];
    for (const [k, w] of map(v)) out[k] = w;
  }
  return out;
}

export function compileItinerary(
  changes: readonly DraftWireChange[],
  base: TourBaseline,
  files?: StagedFiles,
): CompiledItinerary {
  const body: TourWriteInput = {};
  const out: CompiledItinerary['files'] = {};
  const index: ItineraryIndex = { days: [], slots: [], activities: [] };
  const included: DraftWireChange[] = [];
  const missingFiles: CompiledItinerary['missingFiles'] = [];
  const raw = base.itinerary;

  // The draft's entries of the itinerary, as a store would hold them.
  const entries: Entries = {};
  const byRec = new Map<RecordKey, DraftWireChange[]>();
  let treeTouched = false;
  let daysChange: DraftWireChange | null = null;
  let tourKmlChange: DraftWireChange | null = null;
  for (const c of changes) {
    const path = c.field ?? '';
    if (!path) continue;
    const rec = `${c.entity}:${c.id}`;
    if (rec === base.rec) {
      if (path === DAYS_PATH) {
        (entries[rec] ??= {})[path] = { from: c.from, to: c.to };
        daysChange = c;
        treeTouched = true;
      } else if (path === TOUR_KML) tourKmlChange = c;
      continue;
    }
    if (c.entity !== DAY && c.entity !== SLOT && c.entity !== ACTIVITY) continue;
    (entries[rec] ??= {})[path] = { from: c.from, to: c.to };
    byRec.set(rec, [...(byRec.get(rec) ?? []), c]);
    if (path !== SLOT_PIN) treeTouched = true;
  }

  const stage = (path: string, to: unknown, onFile: (f: File) => void): 'file' | 'clear' | 'missing' => {
    if (!isStagedFile(to)) return 'clear';
    const file = files?.get(to);
    if (!file) {
      missingFiles.push({ path, name: to.name });
      return 'missing';
    }
    onFile(file);
    return 'file';
  };

  // ---- 7. the tour-level route file (B6)
  if (tourKmlChange) {
    const r = stage(TOUR_KML, tourKmlChange.to, (f) => (out.tourKml = f));
    if (r === 'clear') body.kml_file = null;
    if (r !== 'missing') included.push(tourKmlChange);
  }

  // ---- 6. pins of saved stops (they do not need the tree)
  const slotPins: Record<string, PinPayload> = {};
  for (const [rec, list] of byRec) {
    const { entity, id } = parseRecordKey(rec);
    if (entity !== SLOT || id < 0 || !raw.slots.has(id)) continue;
    const pin = list.find((c) => c.field === SLOT_PIN);
    if (!pin) continue;
    slotPins[String(id)] = pinPayload(asPin(pin.to));
    if (!treeTouched) included.push(pin);
  }

  // ---- 5. the tree
  if (treeTouched) {
    const byPos: Record<string, Record<string, PinPayload>> = {};
    const kml: NonNullable<CompiledItinerary['files']['kml']> = [];
    const placed = new Set<RecordKey>();
    const tree = itineraryTree(entries, base.paths, base.rec);
    const days: Record<string, unknown>[] = [];
    for (const day of tree) {
      if (day.id > 0 && !raw.days.has(day.id)) continue; // gone: drift reports it
      const di = days.length;
      const dRec = dayRec(day.id);
      placed.add(dRec);
      const dOut = rowFields(day.id, DAY_DEFAULTS, entries[dRec], DAY_WIRE);
      const kmlEntry = entries[dRec]?.[DAY_KML];
      if (kmlEntry) {
        const r = stage(DAY_KML, kmlEntry.to, (f) => kml.push({ dayIndex: di, file: f }));
        if (r === 'clear' && day.id > 0) dOut.kml_file = null;
        if (r === 'missing') placed.delete(dRec); // re-added below for the other fields
      }
      const slotIds: number[] = [];
      const activityIds: number[][] = [];
      const timeslots: Record<string, unknown>[] = [];
      for (const slot of day.slots) {
        if (slot.id > 0 && !raw.slots.has(slot.id)) continue;
        const si = timeslots.length;
        const sRec = slotRec(slot.id);
        placed.add(sRec);
        const sOut = rowFields(slot.id, SLOT_DEFAULTS, entries[sRec], slotWire(slot.id < 0));
        if (slot.id < 0) {
          const pin = entries[sRec]?.[SLOT_PIN]?.to ?? DEFAULT_PIN;
          if (!pinsEqual(pin, DEFAULT_PIN)) (byPos[String(di)] ??= {})[String(si)] = pinPayload(asPin(pin));
        }
        const acts: number[] = [];
        const activities: Record<string, unknown>[] = [];
        for (const aid of slot.activities) {
          if (aid > 0 && !raw.activities.has(aid)) continue;
          const aRec = activityRec(aid);
          placed.add(aRec);
          const aOut = rowFields(aid, ACTIVITY_DEFAULTS, entries[aRec], ACTIVITY_WIRE);
          if (aid < 0) aOut.is_selected = false;
          activities.push(aOut);
          acts.push(aid);
        }
        sOut.activities = activities;
        timeslots.push(sOut);
        slotIds.push(slot.id);
        activityIds.push(acts);
      }
      dOut.timeslots = timeslots;
      days.push(dOut);
      index.days.push(day.id);
      index.slots.push(slotIds);
      index.activities.push(activityIds);
    }
    body.itinerary = { days };
    if (Object.keys(byPos).length > 0) body.slot_pins_by_pos = byPos;
    if (kml.length > 0) out.kml = kml;
    if (daysChange) included.push(daysChange);
    for (const [rec, list] of byRec) {
      if (placed.has(rec)) {
        included.push(...list);
        continue;
      }
      // A day whose staged route file is missing: its other changes still go.
      const { entity, id } = parseRecordKey(rec);
      if (entity === DAY && (id < 0 || raw.days.has(id)) && index.days.includes(id)) {
        included.push(...list.filter((c) => c.field !== DAY_KML));
      }
    }
  }
  if (Object.keys(slotPins).length > 0) body.slot_pins = slotPins;
  return { body, files: out, index, included, missingFiles };
}

/** Whether a change is part of the itinerary (its records, the tour's day list and route). */
export function isItineraryChange(c: Pick<DraftWireChange, 'entity' | 'field'>, tourEntity: string): boolean {
  if (c.entity === DAY || c.entity === SLOT || c.entity === ACTIVITY) return true;
  return c.entity === tourEntity && (c.field === DAYS_PATH || c.field === TOUR_KML);
}
